import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Stack, Accordion, AccordionSummary, AccordionDetails, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Tooltip, IconButton, Chip, Divider } from '@mui/material';
import { ExpandMore, Code, Favorite, ContentCopy, History, Visibility, Share, Colorize, CheckCircle } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { ChromePicker, ColorResult } from 'react-color';
import { useColorHistory, simulateColorBlindness, getContrastRatio, getColorFromURL, setColorToURL, getComplementaryColor, getAnalogousColors } from '../hooks/useColorUtils';

// CSS renk adları ve HEX karşılıkları
const cssColors = [
  { name: 'Black', hex: '#000000' }, { name: 'White', hex: '#FFFFFF' },
  { name: 'Red', hex: '#FF0000' }, { name: 'Lime', hex: '#00FF00' },
  { name: 'Blue', hex: '#0000FF' }, { name: 'Yellow', hex: '#FFFF00' },
  { name: 'Cyan', hex: '#00FFFF' }, { name: 'Magenta', hex: '#FF00FF' },
  { name: 'Silver', hex: '#C0C0C0' }, { name: 'Gray', hex: '#808080' },
  { name: 'Maroon', hex: '#800000' }, { name: 'Olive', hex: '#808000' },
  { name: 'Green', hex: '#008000' }, { name: 'Purple', hex: '#800080' },
  { name: 'Teal', hex: '#008080' }, { name: 'Navy', hex: '#000080' },
  { name: 'Orange', hex: '#FFA500' }, { name: 'Pink', hex: '#FFC0CB' },
  { name: 'Brown', hex: '#A52A2A' }, { name: 'Aqua', hex: '#00FFFF' },
  { name: 'Gold', hex: '#FFD700' }, { name: 'Coral', hex: '#FF7F50' },
  { name: 'Salmon', hex: '#FA8072' }, { name: 'Indigo', hex: '#4B0082' },
  { name: 'Violet', hex: '#EE82EE' }, { name: 'Turquoise', hex: '#40E0D0' },
  { name: 'Beige', hex: '#F5F5DC' }, { name: 'Ivory', hex: '#FFFFF0' },
  { name: 'Khaki', hex: '#F0E68C' }, { name: 'Lavender', hex: '#E6E6FA' },
  { name: 'Plum', hex: '#DDA0DD' }, { name: 'Orchid', hex: '#DA70D6' },
  { name: 'Tan', hex: '#D2B48C' }, { name: 'Chocolate', hex: '#D2691E' },
  { name: 'Crimson', hex: '#DC143C' }, { name: 'FireBrick', hex: '#B22222' },
  { name: 'SlateGray', hex: '#708090' }, { name: 'SkyBlue', hex: '#87CEEB' },
  { name: 'RoyalBlue', hex: '#4169E1' }, { name: 'ForestGreen', hex: '#228B22' },
];

function hexToRgb(hex: string) {
  const match = hex.replace('#', '').match(/.{1,2}/g);
  if (!match) return 'rgb(0,0,0)';
  const [r, g, b] = match.map(x => parseInt(x, 16));
  return `rgb(${r}, ${g}, ${b})`;
}

function hexToHsl(hex: string) {
  let r = 0, g = 0, b = 0;
  if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16) / 255;
    g = parseInt(hex.slice(3, 5), 16) / 255;
    b = parseInt(hex.slice(5, 7), 16) / 255;
  }
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
}

function hexDistance(hex1: string, hex2: string) {
  const r1 = parseInt(hex1.slice(1, 3), 16);
  const g1 = parseInt(hex1.slice(3, 5), 16);
  const b1 = parseInt(hex1.slice(5, 7), 16);
  const r2 = parseInt(hex2.slice(1, 3), 16);
  const g2 = parseInt(hex2.slice(3, 5), 16);
  const b2 = parseInt(hex2.slice(5, 7), 16);
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

function getNearestColorNames(hex: string, count = 3) {
  const sorted = cssColors
    .map(color => ({ ...color, dist: hexDistance(hex.toUpperCase(), color.hex.toUpperCase()) }))
    .sort((a, b) => a.dist - b.dist);
  return sorted.slice(0, count);
}

const ColorPreview = styled(Paper)(({ theme }) => ({
  width: 100,
  height: 100,
  marginRight: theme.spacing(2),
  borderRadius: 10,
  boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
}));

const FAVORITES_KEY = 'favorites_v1';

const ColorPicker: React.FC = () => {
  // URL'den renk al
  const urlColor = getColorFromURL();
  const [color, setColor] = useState<string>(urlColor || '#FF0000');
  const [xamlType, setXamlType] = useState<'name' | 'hex'>('name');
  const [favAdded, setFavAdded] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const { history, addToHistory, clearHistory } = useColorHistory();

  // URL güncelle
  useEffect(() => {
    setColorToURL(color);
  }, [color]);

  const handleColorChange = (c: ColorResult) => {
    setColor(c.hex);
    addToHistory(c.hex);
  };

  const rgb = hexToRgb(color);
  const hsl = hexToHsl(color);
  const nearestColors = getNearestColorNames(color, 3);
  const colorName = nearestColors[0].name;

  // Renk körlüğü simülasyonları
  const colorBlindSimulations = [
    { type: 'protanopia' as const, name: 'Protanopi (Kırmızı Körlüğü)', color: simulateColorBlindness(color, 'protanopia') },
    { type: 'deuteranopia' as const, name: 'Deuteranopi (Yeşil Körlüğü)', color: simulateColorBlindness(color, 'deuteranopia') },
    { type: 'tritanopia' as const, name: 'Tritanopi (Mavi Körlüğü)', color: simulateColorBlindness(color, 'tritanopia') },
    { type: 'achromatopsia' as const, name: 'Akromatopsi (Tam Renk Körlüğü)', color: simulateColorBlindness(color, 'achromatopsia') },
  ];

  // Kontrast kontrolü
  const contrastWhite = getContrastRatio(color, '#FFFFFF');
  const contrastBlack = getContrastRatio(color, '#000000');

  // İlgili renkler
  const complementary = getComplementaryColor(color);
  const analogous = getAnalogousColors(color);

  // XAML için renk adı veya hex seçimine göre
  const xamlBackground = xamlType === 'name' ? `Background="${colorName}"` : `Background="${color.toUpperCase()}"`;
  const xamlForeground = xamlType === 'name' ? `Foreground="${colorName}"` : `Foreground="${color.toUpperCase()}"`;
  const reactStyleSx = xamlType === 'name' ? `sx={{ bgcolor: "${colorName}" }}` : `sx={{ bgcolor: "${color.toUpperCase()}" }}`;
  const reactStyleInline = xamlType === 'name' ? `style={{ backgroundColor: "${colorName}" }}` : `style={{ backgroundColor: "${color.toUpperCase()}" }}`;

  const codeFormats = [
    // XAML & WPF
    { name: 'XAML Background', color: '#1976d2', code: xamlBackground },
    { name: 'XAML Foreground', color: '#88CB70', code: xamlForeground },
    { name: 'XAML Brush', color: '#1976d2', code: `<SolidColorBrush Color="${color.toUpperCase()}"/>` },

    // React & Web
    { name: 'React (sx)', color: '#61dafb', code: reactStyleSx },
    { name: 'React (inline)', color: '#FFD600', code: reactStyleInline },
    { name: 'CSS', color: '#9c27b0', code: `color: ${color};` },
    { name: 'HTML', color: '#795548', code: `style="color: ${color}"` },
    { name: 'SCSS', color: '#C6538C', code: `$my-color: ${color.toUpperCase()};` },
    { name: 'LESS', color: '#1D365D', code: `@my-color: ${color.toUpperCase()};` },
    { name: 'Tailwind CSS', color: '#38BDF8', code: `bg-[${color.toUpperCase()}]` },

    // .NET
    { name: 'C#', color: '#607d8b', code: `Color.FromRgb(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)})` },
    { name: 'VB.NET', color: '#00599c', code: `Color.FromRgb(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)})` },
    { name: '.NET MAUI', color: '#512bd4', code: `Colors.${colorName}` },
    { name: 'Unity C#', color: '#222C37', code: `new Color32(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 255)` },

    // Mobile
    { name: 'Flutter', color: '#02569b', code: `Color(0xFF${color.slice(1).toUpperCase()})` },
    { name: 'Dart', color: '#00B4AB', code: `Color(0xFF${color.slice(1).toUpperCase()})` },
    { name: 'Swift', color: '#F05138', code: `UIColor(red: ${(parseInt(color.slice(1, 3), 16) / 255).toFixed(2)}, green: ${(parseInt(color.slice(3, 5), 16) / 255).toFixed(2)}, blue: ${(parseInt(color.slice(5, 7), 16) / 255).toFixed(2)}, alpha: 1.0)` },
    { name: 'Objective-C', color: '#438EFF', code: `[UIColor colorWithRed:${(parseInt(color.slice(1, 3), 16) / 255).toFixed(2)} green:${(parseInt(color.slice(3, 5), 16) / 255).toFixed(2)} blue:${(parseInt(color.slice(5, 7), 16) / 255).toFixed(2)} alpha:1.0]` },
    { name: 'Android XML', color: '#3DDC84', code: `android:background="${color.toUpperCase()}"` },
    { name: 'Kotlin', color: '#A97BFF', code: `Color.parseColor("${color.toUpperCase()}")` },
    { name: 'Java', color: '#b07219', code: `Color.decode("${color.toUpperCase()}")` },

    // Scripting
    { name: 'Python', color: '#3776ab', code: `color = "${color}"` },
    { name: 'PHP', color: '#777BB4', code: `$color = '${color.toUpperCase()}';` },
    { name: 'Laravel', color: '#FF2D20', code: `style="background-color: {{ '${color.toUpperCase()}' }};"` },
    { name: 'PowerShell', color: '#012456', code: `[System.Drawing.Color]::FromArgb(${parseInt(color.slice(1, 3), 16)},${parseInt(color.slice(3, 5), 16)},${parseInt(color.slice(5, 7), 16)})` },

    // Systems
    { name: 'Rust', color: '#DEA584', code: `let color = (${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)});` },
    { name: 'Go', color: '#00ADD8', code: `color.RGBA{R: ${parseInt(color.slice(1, 3), 16)}, G: ${parseInt(color.slice(3, 5), 16)}, B: ${parseInt(color.slice(5, 7), 16)}, A: 255}` },
    { name: 'C++ (Qt)', color: '#005F87', code: `QColor("${color.toUpperCase()}")` },

    // Other
    { name: 'Excel VBA', color: '#217346', code: `RGB(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)})` },
    { name: 'QML', color: '#41CD52', code: `color: "${color.toUpperCase()}"` },
    { name: 'MATLAB', color: '#E16737', code: `[${(parseInt(color.slice(1, 3), 16) / 255).toFixed(2)} ${(parseInt(color.slice(3, 5), 16) / 255).toFixed(2)} ${(parseInt(color.slice(5, 7), 16) / 255).toFixed(2)}]` },
    { name: 'Delphi', color: '#E37933', code: `TColor($${color.slice(5, 7)}${color.slice(3, 5)}${color.slice(1, 3)})` },

    // Design
    { name: 'Figma', color: '#A259FF', code: `${color.toUpperCase()}` },
    { name: 'SVG', color: '#FFB13B', code: `<rect fill="${color.toUpperCase()}" />` },
  ];

  const handleCopyCode = (code: string, name: string) => {
    navigator.clipboard.writeText(code);
    setCopied(name);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleAddFavorite = () => {
    const fav = {
      id: 'color-' + color,
      type: 'color',
      name: nearestColors[0].name,
      data: { hex: color },
      date: new Date().toISOString(),
    };
    let favs: any[] = [];
    try {
      favs = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
    } catch { }
    if (!favs.some(f => f.id === fav.id)) {
      favs.unshift(fav);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
      window.dispatchEvent(new Event('favoriteAdded')); // FavoritesPanel'i güncelle
      setFavAdded(true);
      setTimeout(() => setFavAdded(false), 1500);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopied('URL');
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <Paper sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#f5f5f5', borderRadius: 2 }}>
      <Stack spacing={2}>
        {/* Üst Kısım - Renk Seçici ve Önizleme */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Sol - Renk Seçici */}
          <Box>
            <ChromePicker color={color} onChange={handleColorChange} disableAlpha={true} />

            {/* Renk Geçmişi */}
            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<History />}
                size="small"
                onClick={() => setShowHistory(!showHistory)}
                sx={{ mb: 1 }}
              >
                Son Kullanılanlar ({history.length})
              </Button>
              {showHistory && (
                <Paper sx={{ p: 1, bgcolor: '#fff' }}>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {history.slice(0, 12).map((h, i) => (
                      <Tooltip key={i} title={h.hex}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: h.hex,
                            borderRadius: 0.5,
                            cursor: 'pointer',
                            border: color === h.hex ? '2px solid #000' : '1px solid #ccc',
                            '&:hover': { transform: 'scale(1.1)' },
                            transition: 'transform 0.2s',
                          }}
                          onClick={() => setColor(h.hex)}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                  {history.length > 0 && (
                    <Button size="small" color="error" onClick={clearHistory} sx={{ mt: 1 }}>
                      Temizle
                    </Button>
                  )}
                </Paper>
              )}
            </Box>
          </Box>

          {/* Orta - Renk Bilgileri */}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <ColorPreview sx={{ bgcolor: color }} />
              <Box>
                <Typography variant="h5" fontWeight={700}>{colorName}</Typography>
                <Typography fontFamily="monospace" fontSize={18}>{color.toUpperCase()}</Typography>
                <Typography variant="body2" color="text.secondary">{rgb}</Typography>
                <Typography variant="body2" color="text.secondary">{hsl}</Typography>
              </Box>
            </Stack>

            {/* Aksiyon Butonları */}
            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" gap={1}>
              <Button
                variant="contained"
                color={favAdded ? 'success' : 'secondary'}
                startIcon={favAdded ? <CheckCircle /> : <Favorite />}
                onClick={handleAddFavorite}
                disabled={favAdded}
              >
                {favAdded ? 'Eklendi!' : 'Favorilere Ekle'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Share />}
                onClick={handleShare}
                color={copied === 'URL' ? 'success' : 'primary'}
              >
                {copied === 'URL' ? 'Kopyalandı!' : 'Paylaş'}
              </Button>
              <Tooltip title="HEX Kodu Kopyala">
                <IconButton onClick={() => handleCopyCode(color, 'HEX')} color={copied === 'HEX' ? 'success' : 'default'}>
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* İlgili Renkler */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>İlgili Renkler</Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title={`Tamamlayıcı: ${complementary}`}>
                  <Box
                    sx={{ width: 36, height: 36, bgcolor: complementary, borderRadius: 1, cursor: 'pointer', border: '1px solid #ccc' }}
                    onClick={() => setColor(complementary)}
                  />
                </Tooltip>
                {analogous.map((c, i) => (
                  <Tooltip key={i} title={`Benzer ${i + 1}: ${c}`}>
                    <Box
                      sx={{ width: 36, height: 36, bgcolor: c, borderRadius: 1, cursor: 'pointer', border: '1px solid #ccc' }}
                      onClick={() => setColor(c)}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* Sağ - Kontrast ve Erişilebilirlik */}
          <Paper sx={{ p: 2, minWidth: 250, bgcolor: '#fff' }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              <Visibility sx={{ mr: 1, verticalAlign: 'middle' }} />
              Erişilebilirlik
            </Typography>

            {/* Kontrast Oranları */}
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Box sx={{ width: 40, height: 40, bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700 }}>Aa</Typography>
                </Box>
                <Box>
                  <Typography variant="body2">Beyaz metin</Typography>
                  <Chip
                    label={`${contrastWhite.toFixed(2)}:1`}
                    size="small"
                    color={contrastWhite >= 4.5 ? 'success' : contrastWhite >= 3 ? 'warning' : 'error'}
                  />
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 40, height: 40, bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
                  <Typography sx={{ color: '#000', fontWeight: 700 }}>Aa</Typography>
                </Box>
                <Box>
                  <Typography variant="body2">Siyah metin</Typography>
                  <Chip
                    label={`${contrastBlack.toFixed(2)}:1`}
                    size="small"
                    color={contrastBlack >= 4.5 ? 'success' : contrastBlack >= 3 ? 'warning' : 'error'}
                  />
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Renk Körlüğü Simülasyonu */}
            <Typography variant="subtitle2" gutterBottom>Renk Körlüğü Simülasyonu</Typography>
            <Stack spacing={1}>
              {colorBlindSimulations.map((sim) => (
                <Stack key={sim.type} direction="row" spacing={1} alignItems="center">
                  <Tooltip title={sim.name}>
                    <Box sx={{ width: 28, height: 28, bgcolor: sim.color, borderRadius: 0.5, border: '1px solid #ccc' }} />
                  </Tooltip>
                  <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>{sim.name.split(' ')[0]}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>

        {/* Kod Formatları */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Code sx={{ mr: 1 }} />
            <Typography variant="h6">Kod Formatları</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel>Kopyalama Şekli</FormLabel>
              <RadioGroup row value={xamlType} onChange={e => setXamlType(e.target.value as 'name' | 'hex')}>
                <FormControlLabel value="name" control={<Radio />} label="Renk Adı" />
                <FormControlLabel value="hex" control={<Radio />} label="HEX Kod" />
              </RadioGroup>
            </FormControl>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1 }}>
              {codeFormats.map((format) => (
                <Paper
                  key={format.name}
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: copied === format.name ? '#e8f5e9' : '#fafafa',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f0f0f0' },
                    transition: 'background-color 0.2s',
                  }}
                  onClick={() => handleCopyCode(format.code, format.name)}
                >
                  <Box>
                    <Chip label={format.name} size="small" sx={{ bgcolor: format.color, color: '#fff', mb: 0.5 }} />
                    <Typography variant="caption" display="block" fontFamily="monospace" noWrap sx={{ maxWidth: 180 }}>
                      {format.code}
                    </Typography>
                  </Box>
                  <IconButton size="small" color={copied === format.name ? 'success' : 'default'}>
                    {copied === format.name ? <CheckCircle /> : <ContentCopy />}
                  </IconButton>
                </Paper>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Paper>
  );
};

export default ColorPicker;